import { useEffect, useState } from 'react';
import { Mail, Loader2, Send, Key as KeyIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAdmin } from '@/hooks/useAdmin';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEmailLog() {
  const { authFetch } = useAdmin();
  const [rows, setRows] = useState([]);
  const [keys, setKeys] = useState({ api_key: '', mock_mode: true, from_email: '' });
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState('');

  const load = () => {
    Promise.all([
      authFetch(`${API}/admin/email/log?limit=100`).then(r => r.json()),
      authFetch(`${API}/admin/email/keys`).then(r => r.json()),
    ]).then(([log, k]) => {
      setRows(log.entries || log.log || (Array.isArray(log) ? log : []));
      setKeys(k);
      setLoading(false);
    });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveKeys = async () => {
    const r = await authFetch(`${API}/admin/email/keys`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(keys),
    });
    r.ok ? toast.success('Spremljeno') : toast.error('Greška');
  };

  const sendTest = async () => {
    if (!testTo) return toast.error('Upišite email');
    const r = await authFetch(`${API}/admin/email/test`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: testTo, subject: 'Test iz e-mina CMS', body: 'Ovo je testni email.' }),
    });
    if (r.ok) { toast.success('Poslano (ili logirano ako je mock)'); load(); } else toast.error('Greška');
  };

  return (
    <div className="p-4 md:p-6" data-testid="admin-email-log">
      <div className="flex items-center gap-3 mb-6">
        <Mail className="text-red-500" size={24} />
        <div>
          <h1 className="text-2xl font-bold">Email sustav</h1>
          <p className="text-sm text-zinc-400">SendGrid postavke i zapis poslanih emaila.</p>
        </div>
      </div>

      {/* Keys */}
      <section className="mb-6 p-4 rounded-lg border border-zinc-800 bg-zinc-900/50">
        <h2 className="text-sm font-bold text-zinc-200 mb-3 inline-flex items-center gap-2"><KeyIcon size={14}/> SendGrid</h2>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">API key</label>
            <input value={keys.api_key || ''} onChange={e => setKeys({ ...keys, api_key: e.target.value })}
                   placeholder="SG.xxxx…" data-testid="email-api-key"
                   className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm font-mono" />
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Pošiljatelj</label>
            <input value={keys.from_email || ''} onChange={e => setKeys({ ...keys, from_email: e.target.value })}
                   placeholder="no-reply@e-mina.eu" data-testid="email-from"
                   className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-200">
            <input type="checkbox" checked={!!keys.mock_mode}
                   onChange={e => setKeys({ ...keys, mock_mode: e.target.checked })}
                   data-testid="email-mock"
                   className="accent-red-600" />
            MOCK način (spremaj u DB umjesto slanja)
          </label>
        </div>
        <button onClick={saveKeys} data-testid="save-email-keys"
                className="mt-3 px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-bold">Spremi</button>
        <div className="mt-4 pt-4 border-t border-zinc-800 flex gap-2">
          <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="test@primjer.hr"
                 data-testid="test-email-to"
                 className="flex-1 max-w-xs bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
          <button onClick={sendTest} data-testid="send-test-email"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded bg-zinc-700 hover:bg-zinc-600 text-sm text-white"><Send size={14}/> Pošalji test</button>
        </div>
      </section>

      {/* Log */}
      <h2 className="text-sm font-bold text-zinc-200 mb-2">Zapis poslanih (zadnjih 100)</h2>
      {loading ? <Loader2 className="animate-spin text-zinc-500" /> : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="min-w-full text-xs">
            <thead className="bg-zinc-900 text-zinc-400 uppercase">
              <tr><th className="px-3 py-2 text-left">Vrijeme</th><th className="px-3 py-2 text-left">Za</th><th className="px-3 py-2 text-left">Predmet</th><th className="px-3 py-2 text-left">Način</th><th className="px-3 py-2 text-left">Status</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i} className="border-t border-zinc-800">
                  <td className="px-3 py-1.5 text-zinc-500 whitespace-nowrap">{(r.created_at || r.sent_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-3 py-1.5 text-zinc-200">{r.to || r.recipient}</td>
                  <td className="px-3 py-1.5 text-zinc-400 truncate max-w-xs">{r.subject}</td>
                  <td className="px-3 py-1.5"><span className="text-[10px] uppercase">{r.mode || (r.mocked ? 'MOCK' : 'SEND')}</span></td>
                  <td className="px-3 py-1.5">{r.success !== false ? <span className="text-emerald-400">OK</span> : <span className="text-red-400">FAIL</span>}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-zinc-500">Prazan zapis.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
